import React, {
    useEffect, useRef, useState, useCallback,
} from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Box, IconButton, Typography } from '@material-ui/core';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';

// Configurar worker de pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const useStyles = makeStyles(() => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#525659',
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0.5rem',
        backgroundColor: '#3E3E3C',
        color: '#FFFFFF',
        zIndex: 10,
    },
    scrollContainer: {
        flex: 1,
        overflow: 'auto',
        padding: '1rem',
    },
    pageWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '1rem',
    },
    canvas: {
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
        cursor: 'crosshair',
        backgroundColor: '#FFFFFF',
    },
    pageInfo: {
        margin: '0 1rem',
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
    },
    iconButton: {
        color: '#FFFFFF',
        '&:disabled': {
            color: '#666666',
        },
    },
    hint: {
        position: 'absolute',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: '#FFFFFF',
        padding: '0.5rem 1rem',
        borderRadius: '0.5rem',
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        zIndex: 100,
    },
}));

function PdfRenderer({
    pdfFile, onPageClick, scale: initialScale,
    showHint, signatureOverlays, onScaleChange,
}) {
    const classes = useStyles();
    const scrollRef = useRef(null);
    const canvasRefs = useRef([]);
    const pageRefs = useRef([]);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [scale, setScale] = useState(initialScale || 1.5);
    const [isLoading, setIsLoading] = useState(true);
    const isScrolling = useRef(false);

    // Cargar documento PDF
    useEffect(() => {
        const loadPdf = async () => {
            try {
                setIsLoading(true);
                let pdfData;

                if (pdfFile instanceof Blob) {
                    pdfData = await pdfFile.arrayBuffer();
                } else if (typeof pdfFile === 'string') {
                    const base64Clean = pdfFile.includes(',')
                        ? pdfFile.split(',')[1]
                        : pdfFile;
                    const binaryString = window.atob(base64Clean);
                    const len = binaryString.length;
                    const bytes = new Uint8Array(len);
                    for (let i = 0; i < len; i += 1) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    pdfData = bytes.buffer;
                }

                const doc = await pdfjsLib.getDocument({
                    data: pdfData,
                }).promise;
                setPdfDoc(doc);
                setTotalPages(doc.numPages);
                canvasRefs.current = new Array(doc.numPages);
                pageRefs.current = new Array(doc.numPages);
                setIsLoading(false);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error cargando PDF:', error);
                setIsLoading(false);
            }
        };

        if (pdfFile) {
            loadPdf();
        }
    }, [pdfFile]);

    // Renderizar todas las paginas
    useEffect(() => {
        const renderAllPages = async () => {
            if (!pdfDoc) return;

            for (let i = 1; i <= totalPages; i += 1) {
                const canvas = canvasRefs.current[i - 1];
                if (canvas) {
                    try {
                        // eslint-disable-next-line no-await-in-loop
                        const page = await pdfDoc.getPage(i);
                        const viewport = page.getViewport({
                            scale,
                        });
                        const context = canvas.getContext('2d');

                        canvas.height = viewport.height;
                        canvas.width = viewport.width;

                        // eslint-disable-next-line no-await-in-loop
                        await page.render({
                            canvasContext: context,
                            viewport,
                        }).promise;
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(
                            `Error renderizando pagina ${i}:`,
                            error,
                        );
                    }
                }
            }
        };

        renderAllPages();
    }, [pdfDoc, totalPages, scale]);

    // Detectar pagina visible al hacer scroll
    const handleScroll = useCallback(() => {
        if (isScrolling.current) return;
        const container = scrollRef.current;
        if (!container) return;

        const { scrollTop } = container;
        const containerMiddle = scrollTop
            + container.clientHeight / 2;

        for (let i = 0; i < totalPages; i += 1) {
            const wrapper = pageRefs.current[i];
            if (wrapper) {
                const top = wrapper.offsetTop;
                const bottom = top + wrapper.offsetHeight;

                if (containerMiddle >= top
                    && containerMiddle < bottom) {
                    setCurrentPage(i + 1);
                    break;
                }
            }
        }
    }, [totalPages]);

    // Scroll a pagina especifica
    const scrollToPage = useCallback((pageNum) => {
        const wrapper = pageRefs.current[pageNum - 1];
        if (!wrapper || !scrollRef.current) return;

        isScrolling.current = true;
        setCurrentPage(pageNum);

        wrapper.scrollIntoView({
            behavior: 'smooth', block: 'start',
        });

        setTimeout(() => {
            isScrolling.current = false;
        }, 500);
    }, []);

    // Click en canvas para posicionar firma
    const handleCanvasClick = useCallback((event, pageIndex) => {
        if (!onPageClick) return;

        const canvas = canvasRefs.current[pageIndex];
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const clickY = event.clientY - rect.top;

        const pdfX = clickX / scale;
        const pdfY = (canvas.height - clickY) / scale;

        onPageClick({
            x: pdfX,
            y: pdfY,
            pageIndex,
            canvasX: clickX,
            canvasY: clickY,
            canvasLeft: rect.left,
            canvasTop: rect.top,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            scale,
        });
    }, [onPageClick, scale]);

    const handlePrevPage = useCallback(() => {
        if (currentPage > 1) {
            scrollToPage(currentPage - 1);
        }
    }, [currentPage, scrollToPage]);

    const handleNextPage = useCallback(() => {
        if (currentPage < totalPages) {
            scrollToPage(currentPage + 1);
        }
    }, [currentPage, totalPages, scrollToPage]);

    const handleZoomIn = useCallback(() => {
        setScale((prev) => {
            const next = Math.min(prev + 0.25, 3);
            if (onScaleChange) onScaleChange(next);
            return next;
        });
    }, [onScaleChange]);

    const handleZoomOut = useCallback(() => {
        setScale((prev) => {
            const next = Math.max(prev - 0.25, 0.5);
            if (onScaleChange) onScaleChange(next);
            return next;
        });
    }, [onScaleChange]);

    if (isLoading) {
        return (
          <Box className={classes.container}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <Typography style={{
                  color: '#FFFFFF',
                  fontFamily: 'Montserrat',
              }}
              >
                Cargando documento...
              </Typography>
            </Box>
          </Box>
        );
    }

    return (
      <Box className={classes.container}>
        <Box className={classes.toolbar}>
          <IconButton
            className={classes.iconButton}
            onClick={handleZoomOut}
            size="small"
          >
            <ZoomOutIcon />
          </IconButton>
          <IconButton
            className={classes.iconButton}
            onClick={handleZoomIn}
            size="small"
          >
            <ZoomInIcon />
          </IconButton>
          <IconButton
            className={classes.iconButton}
            onClick={handlePrevPage}
            disabled={currentPage <= 1}
            size="small"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <Typography className={classes.pageInfo}>
            {currentPage}
            {' '}
            /
            {' '}
            {totalPages}
          </Typography>
          <IconButton
            className={classes.iconButton}
            onClick={handleNextPage}
            disabled={currentPage >= totalPages}
            size="small"
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
        <Box
          className={classes.scrollContainer}
          ref={scrollRef}
          onScroll={handleScroll}
        >
          {Array.from({ length: totalPages }, (_, i) => (
            <Box
              key={`page-${i}`}
              className={classes.pageWrapper}
              ref={(el) => { pageRefs.current[i] = el; }}
            >
              <Box style={{ position: 'relative' }}>
                <canvas
                  ref={(el) => { canvasRefs.current[i] = el; }}
                  data-page-index={i}
                  className={classes.canvas}
                  onClick={(e) => handleCanvasClick(e, i)}
                />
                {signatureOverlays
                  .filter((o) => o.pageIndex === i)
                  .map((o) => o.element)}
              </Box>
            </Box>
          ))}
        </Box>
        {showHint && (
          <Box className={classes.hint}>
            Haga clic en el documento para posicionar su firma
          </Box>
        )}
      </Box>
    );
}

PdfRenderer.defaultProps = {
    onPageClick: null,
    scale: 1.5,
    showHint: true,
    signatureOverlays: [],
    onScaleChange: null,
};

PdfRenderer.propTypes = {
    pdfFile: PropTypes.oneOfType([
        PropTypes.string, PropTypes.object,
    ]).isRequired,
    onPageClick: PropTypes.func,
    scale: PropTypes.number,
    showHint: PropTypes.bool,
    signatureOverlays: PropTypes.arrayOf(PropTypes.shape({
        pageIndex: PropTypes.number,
        element: PropTypes.node,
    })),
    onScaleChange: PropTypes.func,
};

export default PdfRenderer;
